import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { Icon } from './Icon';
import { ThemeToggle } from './ThemeToggle';

/**
 * Mobile sticky header used across learner stack screens.
 * On desktop, screens use their own page headings instead.
 */
export function MobileHeader({
  title,
  th,
  onBack,
  right,
  large = true,
}: {
  title: string;
  th?: string;
  onBack?: () => void;
  right?: ReactNode;
  large?: boolean;
}) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  return (
    <div
      className="md:hidden"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'var(--paper)',
        borderBottom: '1px solid var(--line)',
        paddingTop: 'calc(env(safe-area-inset-top) + 14px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '8px 20px 14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {onBack && (
            <button
              onClick={onBack}
              aria-label="Back"
              style={{
                border: '1px solid var(--line-2)',
                borderRadius: 99,
                padding: 8,
                background: 'var(--card)',
                cursor: 'pointer',
                marginLeft: -2,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <BackIcon />
            </button>
          )}
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: large ? 26 : 20,
                fontWeight: 600,
                letterSpacing: '-.02em',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </h1>
            {th && (
              <div className="th" style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 3 }}>
                {th}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
          <ThemeToggle compact />
          {right}
          <button
            onClick={() => { signOut(); navigate('/welcome'); }}
            title="Sign out"
            aria-label="Sign out"
            style={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 99, border: '1px solid var(--line-2)', background: 'var(--card)', color: 'var(--clay)', cursor: 'pointer', flex: 'none' }}
          >
            <Icon name="logout" size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}

function BackIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
      <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
